'use client';

import type { ProductToCompareT } from '../../../../../../type/product/productComparison';
import { getStockStatusChecks } from '../../../../../../product/index';
import {
  ProductComparisonFilterLabel,
  ProductComparisonLabelCell,
  ProductComparisonLabelCellVariant,
  ProductComparisonName,
  ProductComparisonRow,
  ProductComparisonRowVariant,
  ProductComparisonValueCell,
  ProductComparisonValueCellVariant,
  productComparisonImageClasses,
} from '@vs-shop/commerce-ui/productComparison';
import { Checkbox } from '@vs-shop/ui/checkbox';
import { ImageMissingPlaceholder } from '@vs-shop/ui/imageMissingPlaceholder';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { getDefaultCurrency } from '../../../../../../config/currency';

import { resolveImageUrl } from '@vs-shop/engine/image/resolveImageUrl';

import { productComparisonDrag, productComparisonDrop } from '../../dragState';

import VsImage from '@vs-shop/image-link/Image';
import Link from '@vs-shop/image-link/Link';
import { IntlMoney } from '../../../../../server/intl/Intl';
import ProductInStock from '../../../../../server/page/product/panel/ProductInStock';
import ProductStockTooltip from '../../../../../server/product/card/ProductStockTooltip';
import { ProductStockVariant } from '@vs-shop/commerce-ui/productStock';

import { showProductsComparisonDifferencesOnlyAtom } from '@vs-shop/engine/atom/productsComparison/showProductsComparisonDifferencesOnlyAtom';
import { ImageFilter } from '@vs-shop/engine/image/ImageFilter';
import { useAtom } from '@effect/atom-react';

type Props = {
  products: readonly ProductToCompareT[];
  draggingIndex: number | null;
  dragOverIndex: number | null;
};

function ImageRow({ products, draggingIndex, dragOverIndex }: Props) {
  const intl = useIntl();
  const [showDifferencesOnly, setShowDifferencesOnly] = useAtom(
    showProductsComparisonDifferencesOnlyAtom
  );

  const handleCheckboxChange = useCallback(
    (checked: boolean) => {
      setShowDifferencesOnly(checked);
    },
    [setShowDifferencesOnly]
  );

  return (
    <ProductComparisonRow variant={ProductComparisonRowVariant.PLAIN}>
      <ProductComparisonLabelCell variant={ProductComparisonLabelCellVariant.HEADER_OPEN_TOP}>
        <ProductComparisonFilterLabel htmlFor="show-diffs-only">
          <Checkbox
            id="show-diffs-only"
            checked={showDifferencesOnly}
            onCheckedChange={handleCheckboxChange}
            className="size-5 lap:size-6"
          />
          {intl.formatMessage({
            id: 'product.comparison.checkbox.diffsOnly',
            defaultMessage: 'Zobrazit pouze rozdíly',
          })}
        </ProductComparisonFilterLabel>
      </ProductComparisonLabelCell>
      {products.map((product, index) => {
        const imagePathAbstract = product.imagePath ?? null;
        const imagePathResolved =
          imagePathAbstract !== null
            ? resolveImageUrl(imagePathAbstract, ImageFilter.product_comparison_table_header)
            : null;

        return (
          <Link key={`image-${product.code}`} href={product.routerUrl} title={product.name}>
            <ProductComparisonValueCell
              variant={ProductComparisonValueCellVariant.MEDIA}
              drag={productComparisonDrag(draggingIndex, index)}
              drop={productComparisonDrop(dragOverIndex, index)}
            >
              {imagePathResolved !== null ? (
                <VsImage
                  src={imagePathResolved}
                  alt={product.name}
                  width={250}
                  height={187}
                  className={productComparisonImageClasses}
                />
              ) : (
                <ImageMissingPlaceholder />
              )}
            </ProductComparisonValueCell>
          </Link>
        );
      })}
    </ProductComparisonRow>
  );
}

function NameRow({ products, draggingIndex, dragOverIndex }: Props) {
  const intl = useIntl();

  return (
    <ProductComparisonRow variant={ProductComparisonRowVariant.HOVERABLE}>
      <ProductComparisonLabelCell variant={ProductComparisonLabelCellVariant.HEADER}>
        {intl.formatMessage({
          id: 'productsComparison.infoColumn.name',
          defaultMessage: 'Název',
        })}
      </ProductComparisonLabelCell>
      {products.map((product, index) => (
        <ProductComparisonValueCell
          key={`name-${product.code}`}
          variant={ProductComparisonValueCellVariant.PLAIN}
          drag={productComparisonDrag(draggingIndex, index)}
          drop={productComparisonDrop(dragOverIndex, index)}
        >
          <ProductComparisonName render={<Link href={product.routerUrl} title={product.name} />}>
            {product.name}
          </ProductComparisonName>
        </ProductComparisonValueCell>
      ))}
    </ProductComparisonRow>
  );
}

function PriceRow({ products, draggingIndex, dragOverIndex }: Props) {
  const intl = useIntl();
  const currency = getDefaultCurrency();

  return (
    <ProductComparisonRow variant={ProductComparisonRowVariant.HOVERABLE}>
      <ProductComparisonLabelCell variant={ProductComparisonLabelCellVariant.HEADER}>
        {intl.formatMessage({
          id: 'productsComparison.infoColumn.price',
          defaultMessage: 'Cena',
        })}
      </ProductComparisonLabelCell>
      {products.map((product, index) => (
        <ProductComparisonValueCell
          key={`price-${product.code}`}
          variant={ProductComparisonValueCellVariant.FIGURE}
          drag={productComparisonDrag(draggingIndex, index)}
          drop={productComparisonDrop(dragOverIndex, index)}
        >
          <IntlMoney value={{ amount: product.price, currency }} />
        </ProductComparisonValueCell>
      ))}
    </ProductComparisonRow>
  );
}

function StockRow({ products, draggingIndex, dragOverIndex }: Props) {
  const intl = useIntl();

  return (
    <ProductComparisonRow variant={ProductComparisonRowVariant.HOVERABLE}>
      <ProductComparisonLabelCell variant={ProductComparisonLabelCellVariant.HEADER}>
        {intl.formatMessage({
          id: 'productsComparison.infoColumn.stock',
          defaultMessage: 'Skladem',
        })}
      </ProductComparisonLabelCell>
      {products.map((product, index) => {
        const { stock } = product;
        const { isSoldOut } = getStockStatusChecks(stock.status);

        return (
          <ProductComparisonValueCell
            key={`stock-${product.code}`}
            variant={ProductComparisonValueCellVariant.PLAIN}
            drag={productComparisonDrag(draggingIndex, index)}
            drop={productComparisonDrop(dragOverIndex, index)}
          >
            {isSoldOut ? (
              <span>
                {intl.formatMessage({
                  id: 'productCard.stockAndFreeShipping.notInStock',
                  defaultMessage: 'Vyprodáno',
                })}
              </span>
            ) : (
              <ProductStockTooltip stock={stock}>
                <ProductInStock stock={stock} variant={ProductStockVariant.PLAIN} />
              </ProductStockTooltip>
            )}
          </ProductComparisonValueCell>
        );
      })}
    </ProductComparisonRow>
  );
}

function BrandRow({ products, draggingIndex, dragOverIndex }: Props) {
  const intl = useIntl();

  return (
    <ProductComparisonRow variant={ProductComparisonRowVariant.HOVERABLE}>
      <ProductComparisonLabelCell variant={ProductComparisonLabelCellVariant.HEADER}>
        {intl.formatMessage({
          id: 'productsComparison.infoColumn.brand',
          defaultMessage: 'Značka',
        })}
      </ProductComparisonLabelCell>
      {products.map((product, index) => (
        <ProductComparisonValueCell
          key={`brand-${product.code}`}
          variant={ProductComparisonValueCellVariant.PLAIN}
          drag={productComparisonDrag(draggingIndex, index)}
          drop={productComparisonDrop(dragOverIndex, index)}
        >
          {product.brand?.name ?? ''}
        </ProductComparisonValueCell>
      ))}
    </ProductComparisonRow>
  );
}

export default function ProductComparisonProductHeaderRows({
  products,
  draggingIndex,
  dragOverIndex,
}: Props) {
  return (
    <>
      <ImageRow products={products} draggingIndex={draggingIndex} dragOverIndex={dragOverIndex} />
      <NameRow products={products} draggingIndex={draggingIndex} dragOverIndex={dragOverIndex} />
      <PriceRow products={products} draggingIndex={draggingIndex} dragOverIndex={dragOverIndex} />
      <StockRow products={products} draggingIndex={draggingIndex} dragOverIndex={dragOverIndex} />
      <BrandRow products={products} draggingIndex={draggingIndex} dragOverIndex={dragOverIndex} />
    </>
  );
}
